// Pilbara predictive maintenance blog post — BLOG-003
import { ArrowRight } from "lucide-react";
import Nav from "../../components/Nav";
import Footer from "../../components/Footer";
import PostHero from "../../components/PostHero";
import Reveal from "../../components/Reveal";

export default function PilbaraPredictiveMaintenance() {
  return (
    <div className="bg-black">
      <Nav route="inner" />

      <PostHero
        category="Mining"
        title="From telematics to copilots: predictive maintenance for Pilbara fleets"
        author="Industry · Mining"
        date="April 2026"
        readTime="5 min read"
        excerpt="Pilbara and Goldfields operators are sitting on years of telematics data
         that could be preventing unplanned downtime today. Here is the engineering
         path from raw sensor feeds to a maintenance copilot your crews actually use —
         built for WA conditions, with your data staying in Australia."
      />

      <main>
        <div className="max-w-3xl mx-auto px-6 py-12">

          <Reveal>
            <h2 className="font-instrument text-2xl md:text-3xl text-white tracking-tight mt-2">
              The data is already there
            </h2>
          </Reveal>
          <Reveal delay={120}>
            <p className="text-white/70 text-base leading-relaxed mt-4">
              Most iron ore and gold operations across the Pilbara and the Goldfields
              are already collecting everything predictive maintenance needs. Engine
              temperature, payload cycles, fault codes, oil analysis results, tyre
              pressure, GPS position — every modern haul truck, dozer and drill on a
              WA fleet streams telemetry by default. The gap is not sensors. It is
              the <a href="/services/ai-integration.html" className="text-white/90 underline decoration-white/30 underline-offset-4 hover:decoration-white/70 transition-colors">AI integration</a> layer
              that turns years of accumulated telematics history into maintenance
              decisions a planner can act on before the next swing.
            </p>
            <p className="text-white/70 text-base leading-relaxed mt-4">
              The common workaround is manual. A planner — in the Perth office or on
              site — reviews exception reports each morning and decides which assets
              to flag for inspection. That holds until the fleet grows past roughly
              100 assets, the planner rotates off roster, or the exception backlog
              outpaces the hours available to review it. The decision takeaway is
              simple: if your site already runs OEM telematics, the build-versus-wait
              question is settled. The data asset exists, and the cost of waiting is
              unplanned downtime you are already paying for.
            </p>
          </Reveal>

          <div className="hairline my-12" />

          <Reveal>
            <h2 className="font-instrument text-2xl md:text-3xl text-white tracking-tight mt-2">
              What predictive maintenance actually requires
            </h2>
          </Reveal>
          <Reveal delay={120}>
            <p className="text-white/70 text-base leading-relaxed mt-4">
              Predictive maintenance in WA is not a single model. It is a pipeline
              that has to survive site reality: satellite backhaul that drops during
              weather events, sensors caked in dust, pit temperatures past 45 °C,
              and crews changing over every two weeks. You need sensor data that
              stays clean and continuous through all of that. You need a baseline of
              normal operating behaviour per asset class and age profile. You need a
              model that flags deviation early enough for the maintenance team to
              schedule the work. And the output has to land somewhere the right
              person already looks.
            </p>
            <p className="text-white/70 text-base leading-relaxed mt-4">
              That last point is where most implementations fail. A model that
              writes predictions to a database table nobody queries is a data
              exercise, not a maintenance system. The failure mode is integration,
              not modelling — which is why the takeaway for procurement is to score
              vendors on how they wire outputs into your existing CMMS and shift
              workflow, not on accuracy claims from a slide deck. This is the core
              of how we approach <a href="/industries/mining.html" className="text-white/90 underline decoration-white/30 underline-offset-4 hover:decoration-white/70 transition-colors">AI for mining in Western Australia</a> —
              the model is the smallest part of the build.
            </p>
          </Reveal>

          <div className="hairline my-12" />

          <Reveal>
            <h2 className="font-instrument text-2xl md:text-3xl text-white tracking-tight mt-2">
              A 12-week path from prototype to production
            </h2>
          </Reveal>
          <Reveal delay={120}>
            <p className="text-white/70 text-base leading-relaxed mt-4">
              Weeks one to two are the data audit. We connect to your telematics
              platform, review data quality across asset types and identify which
              assets carry enough history for a reliable model. Most fleets have
              good data on high-utilisation assets and patchy data on the rest.
              Start where the data is good. All processing runs in Australian cloud
              regions — data residency in Australia is a scoping requirement, not an
              afterthought, and it should be written into your vendor contract
              before any data moves.
            </p>
            <p className="text-white/70 text-base leading-relaxed mt-4">
              Weeks three to six are model development and validation. We build
              anomaly-detection models per asset class, validate them against known
              failure events in your maintenance history and set alert thresholds
              with your maintenance leads. This step needs direct input from your
              experienced planners and fitters — they know which failure modes
              matter and which are routine noise — so we schedule validation
              sessions around swing changeovers rather than fighting the roster.
              The same pipeline applies beyond haul fleets: it is the approach we
              take to <a href="/industries/machinery.html" className="text-white/90 underline decoration-white/30 underline-offset-4 hover:decoration-white/70 transition-colors">heavy machinery and OEM equipment</a> generally.
            </p>
            <p className="text-white/70 text-base leading-relaxed mt-4">
              Weeks seven to twelve take the system from prototype to production.
              We wire model outputs into the daily workflow — typically the planning
              dashboard your coordinators already use, plus a mobile alert for the
              operator on shift that degrades gracefully when site connectivity
              drops. We run in shadow mode alongside your existing process for two
              to four weeks before going live, so the maintenance team can evidence
              the system against real events before trusting it.
            </p>
          </Reveal>

          <div className="hairline my-12" />

          <Reveal>
            <h2 className="font-instrument text-2xl md:text-3xl text-white tracking-tight mt-2">
              What operators need from a maintenance copilot
            </h2>
          </Reveal>
          <Reveal delay={120}>
            <p className="text-white/70 text-base leading-relaxed mt-4">
              An operator copilot is not a dashboard. It is a decision aid that
              gives the operator one clear recommendation at the right moment.
              "This haul truck is showing early signs of differential bearing wear.
              Flag for inspection before next shift." That is useful at 03:00 on
              night shift, four hours from the nearest workshop. A probability
              distribution with six contributing features is not.
            </p>
            <p className="text-white/70 text-base leading-relaxed mt-4">
              The language matters. Operators trust recommendations that match how
              they already think about equipment. If your experienced fitters
              describe bearing issues in terms of vibration signature and oil
              temperature trending, the copilot should use those terms, not model
              jargon. The takeaway when you scope a build: put your best fitter in
              the room when alert wording is decided. Adoption is won or lost there,
              not in the model architecture.
            </p>
          </Reveal>

          <div className="hairline my-12" />

          <Reveal>
            <h2 className="font-instrument text-2xl md:text-3xl text-white tracking-tight mt-2">
              What to expect in the first quarter
            </h2>
          </Reveal>
          <Reveal delay={120}>
            <p className="text-white/70 text-base leading-relaxed mt-4">
              In the first quarter post-deployment, expect to catch two to four
              developing failures on your high-utilisation assets before they become
              unplanned downtime. The model will also generate false positives —
              alerts that turn into inspections with no fault found. That is
              expected, not a failure. The ratio of true to false positives improves
              as the model accumulates operational data through a full WA seasonal
              cycle, including the wet.
            </p>
            <p className="text-white/70 text-base leading-relaxed mt-4">
              The leading indicator to track in quarter one is not downtime
              reduction. It is inspection efficiency. Are your planners spending
              less time triaging exception reports? Are operators raising fewer
              emergency work orders? Those signals move first — and they are the
              evidence you take to the next budget cycle to scale the system across
              the rest of the fleet. That is what AI transformation looks like in
              practice: one production system, measured honestly, then extended.
            </p>
          </Reveal>

        </div>
      </main>

      <section className="bg-black px-6 pb-24 mt-4">
        <div className="max-w-2xl mx-auto liquid-glass rounded-3xl p-10 text-center">
          <h3 className="font-instrument text-3xl text-white">
            Running a fleet in the Pilbara?
          </h3>
          <p className="text-white/60 text-base mt-4">
            We audit your telematics data and scope a predictive maintenance build
            in a two-week discovery — delivered from Perth, with your data staying
            in Australia. No commitment required.
          </p>
          <a
            href="/#contact"
            className="mt-8 inline-flex items-center gap-2 rounded-full px-8 py-3 bg-white text-black text-sm font-medium hover:bg-white/90 transition-colors"
          >
            Talk to an engineer <ArrowRight size={16} />
          </a>
          <div>
            <a href="/industries/mining.html" className="mt-4 inline-flex items-center gap-2 liquid-glass rounded-full px-8 py-3 text-white text-sm font-medium hover:bg-white/5 transition-colors">AI for Mining &amp; Resources <ArrowRight size={16} /></a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
